/**
 * <localized-money> — locale-aware price display.
 *
 * Server renders Shopify's money format inside the element as the fallback
 * (SEO / no-JS / first paint). On connect the element reformats the amount
 * with Intl.NumberFormat for the document locale, which places the currency
 * symbol correctly per language (de: "11,95 €", fr: "11,95 €", en: "€11.95").
 *
 *   <localized-money data-amount="1195" data-currency="EUR">€11,95</localized-money>
 *
 * data-amount is Shopify's integer subunit value; decimals are derived from
 * the currency itself (so 0-decimal currencies like JPY stay correct).
 * data-with-code="true" appends the ISO code (money_with_currency parity).
 *
 * Morph safety: Horizon updates prices by re-rendering sections and morphing
 * the DOM. The morph rewrites this element's text (server format) and, when
 * the price changed, its data-amount. Both paths re-trigger formatting:
 * attribute changes via attributeChangedCallback, text rewrites via a
 * guarded MutationObserver. data-amount is always the source of truth and
 * always ships alongside the text in the same server render, so the two can
 * never disagree. Scripts that replace whole nodes (e.g. the personaliser's
 * ref writes) remove the element entirely, which safely degrades to their
 * own formatting — no stale reformatting can occur.
 *
 * Purely presentational: no pricing, conversion or rounding logic.
 */
(function () {
  'use strict';

  const locale = (document.documentElement.lang || 'en').trim() || 'en';

  class LocalizedMoney extends HTMLElement {
    static observedAttributes = ['data-amount', 'data-currency'];

    #observer = null;
    #applying = false;
    #lastRendered = null;

    connectedCallback() {
      this.#format();
      this.#observer = new MutationObserver(() => {
        if (this.#applying) return;
        if (this.textContent !== this.#lastRendered) this.#format();
      });
      this.#observer.observe(this, { childList: true, characterData: true, subtree: true });
    }

    disconnectedCallback() {
      this.#observer?.disconnect();
      this.#observer = null;
    }

    attributeChangedCallback(_name, oldValue, newValue) {
      if (oldValue !== newValue && this.isConnected) this.#format();
    }

    #format() {
      const raw = this.getAttribute('data-amount');
      const currency = this.getAttribute('data-currency');
      if (raw === null || raw === '' || !currency) return;
      const subunits = Number(raw);
      if (!Number.isFinite(subunits)) return;

      let formatter;
      try {
        formatter = new Intl.NumberFormat(locale, { style: 'currency', currency: currency });
      } catch (e) {
        return; // unknown currency code: keep the server-rendered fallback
      }
      const digits = formatter.resolvedOptions().maximumFractionDigits;
      const amount = subunits / Math.pow(10, digits);
      let text = formatter.format(amount);
      if (this.getAttribute('data-with-code') === 'true') text += ' ' + currency;

      if (this.textContent === text) {
        this.#lastRendered = text;
        return;
      }
      this.#applying = true;
      this.textContent = text;
      this.#lastRendered = text;
      this.#applying = false; // safe: observer no-ops when text matches #lastRendered
    }
  }

  if (!customElements.get('localized-money')) {
    customElements.define('localized-money', LocalizedMoney);
  }
})();
